import React, { useState, useEffect } from 'react';
import { RefreshCw } from 'lucide-react';

export default function OptionChain({ onSelectOption }) {
  const [symbol, setSymbol] = useState('NIFTY');
  const [expiry, setExpiry] = useState('13-AUG-2026');

  // Professional NSE-style Option Chain data structure
  const [optionData, setOptionData] = useState([
    { strike: 24300, ceLtp: 185.5, ceOi: 45200, ceChgOi: '+12.4K', peLtp: 45.2, peOi: 112000, peChgOi: '-8.5K', iv: '14.2%' },
    { strike: 24350, ceLtp: 152.0, ceOi: 68400, ceChgOi: '+8.1K', peLtp: 62.0, peOi: 98000, peChgOi: '-4.2K', iv: '13.8%' },
    { strike: 24400, ceLtp: 118.4, ceOi: 145000, ceChgOi: '+25.6K', peLtp: 89.5, peOi: 154000, peChgOi: '+15.3K', iv: '13.5%' },
    { strike: 24450, ceLtp: 84.2, ceOi: 92000, ceChgOi: '-3.2K', peLtp: 124.0, peOi: 89000, peChgOi: '+18.7K', iv: '14.0%' },
    { strike: 24500, ceLtp: 55.6, ceOi: 210000, ceChgOi: '+45.2K', peLtp: 168.2, peOi: 72000, peChgOi: '-12.1K', iv: '14.6%' }
  ]);

  // Live Tick Simulation for LTP & OI
  useEffect(() => {
    const interval = setInterval(() => {
      setOptionData(prevData =>
        prevData.map(row => {
          const ceTick = (Math.random() * 2) - 1;
          const peTick = (Math.random() * 2) - 1;
          return {
            ...row,
            ceLtp: Math.max(0.5, +(row.ceLtp + ceTick).toFixed(2)),
            peLtp: Math.max(0.5, +(row.peLtp + peTick).toFixed(2)),
            ceOi: Math.max(1000, row.ceOi + Math.floor(ceTick * 100)),
            peOi: Math.max(1000, row.peOi + Math.floor(peTick * 100)),
          };
        })
      );
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div style={{ padding: '30px', backgroundColor: 'var(--bg-main)', color: 'var(--text-main)', minHeight: '100vh', fontFamily: "'Plus Jakarta Sans', sans-serif", boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', flexWrap: 'wrap', gap: '15px' }}>
        <div>
          <h2 style={{ fontFamily: "'Cinzel', serif", fontSize: '26px', color: 'var(--vyodha-gold)', margin: 0 }}>Institutional Option Chain (NSE Standard)</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '13px', margin: '5px 0 0 0' }}>Real-time OI build-up, Chg in OI & Click-to-Trade execution</p>
        </div>

        <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
          <select 
            value={symbol} 
            onChange={(e) => setSymbol(e.target.value)}
            style={{ backgroundColor: 'var(--bg-panel)', color: 'var(--text-main)', border: '1px solid rgba(212, 175, 55, 0.4)', padding: '10px 15px', borderRadius: '10px', outline: 'none', fontWeight: 'bold' }}
          >
            <option value="NIFTY">NIFTY 50</option>
            <option value="BANKNIFTY">BANK NIFTY</option>
            <option value="FINNIFTY">FIN NIFTY</option>
          </select>

          <select 
            value={expiry} 
            onChange={(e) => setExpiry(e.target.value)}
            style={{ backgroundColor: 'var(--bg-panel)', color: 'var(--text-main)', border: '1px solid rgba(212, 175, 55, 0.4)', padding: '10px 15px', borderRadius: '10px', outline: 'none' }}
          >
            <option value="13-AUG-2026">13 AUG 2026</option>
            <option value="20-AUG-2026">20 AUG 2026</option>
            <option value="27-AUG-2026">27 AUG 2026</option>
          </select>

          <button onClick={() => alert("Option Chain Synced with Live Feed!")} style={{ backgroundColor: 'rgba(212, 175, 55, 0.1)', border: '1px solid #D4AF37', color: '#D4AF37', padding: '10px 15px', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <RefreshCw size={16} /> Sync
          </button>
        </div>
      </div>

      <div style={{ overflowX: 'auto', backgroundColor: 'var(--bg-panel)', borderRadius: '16px', border: '1px solid var(--border-color)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'center', fontSize: '13px' }}>
          <thead>
            <tr style={{ backgroundColor: 'var(--bg-panel-solid)', color: 'var(--text-muted)', borderBottom: '1px solid var(--border-color)' }}>
              <th colSpan="4" style={{ padding: '14px', borderRight: '1px solid var(--border-color)', color: '#38BDF8' }}>CALLS (CE)</th>
              <th style={{ padding: '14px', color: '#D4AF37' }}>STRIKE</th>
              <th colSpan="4" style={{ padding: '14px', borderLeft: '1px solid var(--border-color)', color: '#EF4444' }}>PUTS (PE)</th>
            </tr>
            <tr style={{ backgroundColor: 'var(--bg-main)', color: 'var(--text-muted)', fontSize: '11px', borderBottom: '1px solid var(--border-color)' }}>
              <th style={{ padding: '8px' }}>CHG IN OI</th>
              <th>OI</th>
              <th>LTP (₹)</th>
              <th style={{ borderRight: '1px solid var(--border-color)' }}>IV</th>
              <th style={{ color: '#D4AF37' }}>PRICE</th>
              <th style={{ borderLeft: '1px solid var(--border-color)' }}>IV</th>
              <th>LTP (₹)</th>
              <th>OI</th>
              <th>CHG IN OI</th>
            </tr>
          </thead>
          <tbody>
            {optionData.map((row, idx) => {
              const isAtm = row.strike === 24400;
              return (
                <tr 
                  key={idx} 
                  style={{ 
                    borderBottom: '1px solid var(--border-color)',
                    backgroundColor: isAtm ? 'rgba(212, 175, 55, 0.08)' : 'transparent'
                  }}
                >
                  <td style={{ padding: '12px', color: row.ceChgOi.includes('+') ? '#10B981' : '#EF4444', fontSize: '12px' }}>{row.ceChgOi}</td>
                  <td style={{ padding: '12px', color: 'var(--text-main)' }}>{row.ceOi.toLocaleString()}</td>
                  
                  {/* Clickable CE LTP */}
                  <td 
                    onClick={() => onSelectOption && onSelectOption({ strike: row.strike, type: 'CE', price: row.ceLtp })} 
                    style={{ padding: '12px', fontWeight: 'bold', color: '#38BDF8', cursor: 'pointer', backgroundColor: 'rgba(56, 189, 248, 0.05)' }}
                    title="Click to Trade CE"
                  >
                    {row.ceLtp} ⚡
                  </td>

                  <td style={{ padding: '12px', borderRight: '1px solid var(--border-color)', color: 'var(--text-muted)' }}>{row.iv}</td>
                  
                  <td style={{ padding: '12px', fontWeight: 'bold', color: isAtm ? '#D4AF37' : 'var(--text-main)', backgroundColor: isAtm ? 'rgba(212, 175, 55, 0.15)' : 'var(--bg-main)' }}>
                    {row.strike} {isAtm && <span style={{ fontSize: '9px', display: 'block', color: '#D4AF37' }}>(ATM)</span>}
                  </td>

                  <td style={{ padding: '12px', borderLeft: '1px solid var(--border-color)', color: 'var(--text-muted)' }}>{row.iv}</td>
                  
                  {/* Clickable PE LTP */}
                  <td 
                    onClick={() => onSelectOption && onSelectOption({ strike: row.strike, type: 'PE', price: row.peLtp })} 
                    style={{ padding: '12px', fontWeight: 'bold', color: '#EF4444', cursor: 'pointer', backgroundColor: 'rgba(239, 68, 68, 0.05)' }}
                    title="Click to Trade PE"
                  >
                    {row.peLtp} ⚡
                  </td>

                  <td style={{ padding: '12px', color: 'var(--text-main)' }}>{row.peOi.toLocaleString()}</td>
                  <td style={{ padding: '12px', color: row.peChgOi.includes('+') ? '#10B981' : '#EF4444', fontSize: '12px' }}>{row.peChgOi}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}